import React from 'react';
import { Button, Drawer, Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import { Close } from '@carbon/icons-react';

/**
 * Banner component for displaying a question with guidance text
 *
 * @param {Object} props
 * @param {string} props.question - The main question text
 * @param {React.ReactNode} props.guidance - Guidance text (can include React elements)
 * @param {string} props.className - Additional classes
 * @param {string} props.variant - Variant type (default is 'standalone', can be 'inline', or 'card-title')
 */
const Banner = ({ question, guidance, className, variant = 'standalone', ...rest }) => {
  const isCardTitle = variant === 'card-title';
  const isInline = variant === 'inline';

  return (
    <div
      className={classNames(
        'rounded-xl relative',
        !isInline && !isCardTitle
          ? 'p-8 bg-gradient-to-b from-white to-[#fcfcff] border-l-4 border-solid border-l-[#4654fc] shadow-[0_6px_16px_rgba(0,0,0,0.08)]'
          : '',
        className,
      )}
      {...rest}
    >
      <div className={!isInline && !isCardTitle ? (!!guidance ? 'py-6' : 'py-3') : ''}>
        <Typography.Title
          level={2}
          className={classNames(isCardTitle ? '!text-2xl font-bold text-wrap' : '!text-3xl font-bold', {
            '!mb-2': isCardTitle && !!guidance,
            '!mb-3': !isCardTitle && !!guidance,
            '!mb-0': !guidance,
          })}
        >
          {question}
        </Typography.Title>

        {guidance && (
          <div className={classNames('text-gray-600 font-normal text-wrap', isCardTitle ? 'text-base' : 'text-lg')}>
            {guidance}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Term component for displaying tooltips on hover
 * Used within guidance text
 */
const Term = ({ children, tooltip }) => {
  const [showDrawer, setShowDrawer] = React.useState(false);

  return (
    <>
      <span className="hidden md:inline-block">
        <Tooltip title={tooltip} placement="top">
          <span className="border-b border-dashed border-gray-400 cursor-help">{children}</span>
        </Tooltip>
      </span>

      <span
        className={classNames('inline-block md:hidden', 'border-b border-dashed border-gray-400 cursor-help')}
        onClick={() => {
          setShowDrawer(true);
        }}
      >
        {children}
      </span>

      <Drawer
        title={<span className="font-semibold">{children}</span>}
        extra={
          <Button
            type="text"
            icon={<Close className="w-6 h-6 text-[#64748b]" />}
            className="mr-[-12px]"
            onClick={() => {
              setShowDrawer(false);
            }}
          />
        }
        footer={
          <div className="flex justify-end py-2">
            <Button
              type="primary"
              onClick={() => {
                setShowDrawer(false);
              }}
            >
              Got it
            </Button>
          </div>
        }
        placement="bottom"
        closeIcon={false}
        maskClosable={true}
        onClose={() => {
          setShowDrawer(false);
        }}
        open={showDrawer}
        height="max-content"
        className="visible"
        styles={{
          wrapper: {
            maxHeight: '50vh',
          },
        }}
      >
        <span className="text-[15px] leading-[1.5] text-[#334155]">{tooltip}</span>
      </Drawer>
    </>
  );
};

// Export both components
export { Banner, Term };
